// Lives in its own module so views (e.g. SignUp.vue) can validate a redirect_to
// without importing the whole router. Re-exported from ./index.js.

// Everything we hand to window.location.assign() has to be a path on THIS
// origin. to.fullPath is not that by construction: vue-router preserves a
// leading '//' (it only logs "A resolved location cannot start with multiple
// slashes") and matches it against the lazy catch-all at the bottom of the
// route table — exactly the kind of route that produces a stale-chunk error in
// the first place. window.location.assign('//evil.example.com/x') is then a
// protocol-relative cross-origin navigation, an open redirect the original
// window.location.reload() could not have.
//
// The rejected shapes below all follow from three behaviours of the WHATWG URL
// parser that the browser applies to the string before anything else:
//
//   1. It DELETES every ASCII tab (U+0009), LF (U+000A) and CR (U+000D) from
//      anywhere in the input. So a string that looks like a path can parse as
//      an authority: '/\t/evil.example.com' becomes '//evil.example.com'.
//   2. It STRIPS leading and trailing C0 controls (U+0000–U+001F) and spaces.
//      So ' //evil.example.com' and '\t//evil.example.com' become
//      '//evil.example.com' too.
//   3. For special schemes — and the document is always http(s) here — it
//      treats '\' exactly like '/'. So '/\evil.example.com' is also
//      '//evil.example.com'.
//
// Percent-encoded forms need no handling: percent-decoding happens after
// parsing, so '/%2F%2Fevil.example.com' stays a same-origin path. We
// deliberately do NOT decode before checking — decoding first would invent an
// attack surface rather than close one.
//
// The class below is every C0 control plus DEL, and '\'. Note a raw space is
// NOT in it: vue-router only percent-encodes when it resolves an object
// location, so router.push('/patient/a b') keeps the space verbatim in
// fullPath. A space is never deleted by the parser and is only stripped at the
// ends, so one in the middle of a path cannot affect the origin — rejecting it
// would cost a correct destination for no security gain. Leading and trailing
// spaces are rejected separately below.
// eslint-disable-next-line no-control-regex
const URL_PARSER_UNSAFE_RE = /[\u0000-\u001F\u007F\\]/

export function isSafeInternalPath(path) {
  // Not a usable path at all. '' re-resolves the document URL rather than
  // navigating, and a non-string would be stringified into something arbitrary.
  if (typeof path !== 'string' || path === '') {
    return false
  }
  // Rejects (1) and (3) above: a tab/LF/CR the parser deletes, or a backslash
  // it reads as a slash, can turn a path into an authority. The other C0
  // controls and DEL cannot do that, but no legitimate route path carries one
  // raw, and telling them apart would buy nothing.
  if (URL_PARSER_UNSAFE_RE.test(path)) {
    return false
  }
  // Half of (2): a trailing space is stripped, so the string we validated
  // would not be the string that navigates. A *leading* space — and any
  // leading control — is covered by the leading-'/' requirement below.
  if (path.endsWith(' ')) {
    return false
  }
  // Must be rooted at this origin. This single check also rejects every
  // scheme-ful string — 'https://…', 'HtTpS://…', 'javascript:…', 'data:…' —
  // because a URL scheme may only contain ALPHA / DIGIT / '+' / '-' / '.', so
  // a string that starts with '/' can never carry one. Case-insensitivity of
  // scheme matching therefore costs us nothing. It also rejects schemeless
  // relative paths like 'evil.example.com/x', which would resolve against the
  // current directory rather than the root.
  if (path[0] !== '/') {
    return false
  }
  // '//' opens an authority component: '//evil.example.com/x' is a
  // protocol-relative cross-origin URL. The '\' twin of this is already gone
  // via URL_PARSER_UNSAFE_RE above.
  if (path[1] === '/') {
    return false
  }
  return true
}

// A redirect_to that rides in an emailed link or a query string is held to a
// stricter standard than a path we navigate to ourselves: it is bounded in
// length, and it may not point at /logout (a link that verifies and then
// immediately signs out), /login, or /signup (which would nest the flow).
// Keep in agreement with safe_redirect_path in backend/lib/util/redirect.py.
export const MAX_REDIRECT_TO_LENGTH = 512
const REDIRECT_DENYLIST = ['/logout', '/login', '/signup']

export function isSafeRedirectTarget(path) {
  if (!isSafeInternalPath(path) || path.length > MAX_REDIRECT_TO_LENGTH) {
    return false
  }
  const pathname = path.split(/[?#]/, 1)[0].toLowerCase().replace(/\/+$/, '')
  return !REDIRECT_DENYLIST.includes(pathname)
}
